import { useEffect, useRef } from 'react';

/**
 * setInterval that only runs while the tab is actually being looked at.
 *
 * Background tabs were polling at full rate all day. With notifications and
 * workflow counts on 30s timers, a user who leaves the app open in a tab they
 * never return to still costs the backend four requests a minute indefinitely —
 * and a request that reaches the database is never free, however fast it is.
 *
 * On becoming visible again the callback fires immediately, so coming back to
 * the tab shows fresh data rather than data up to one interval stale. That is
 * usually *better* than plain polling, which would have made you wait.
 *
 * Note both notification bells also hold a websocket for real-time updates, so
 * pausing the poll does not mean missing events while hidden.
 *
 * @param {Function} callback   invoked on each tick; may be recreated freely
 * @param {number}   intervalMs polling period in ms
 * @param {boolean}  enabled    set false to suspend entirely (default true)
 */
export function useVisiblePolling(callback, intervalMs, enabled = true) {
  // Held in a ref so a caller passing an inline arrow does not tear down and
  // recreate the interval on every render.
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled) return undefined;

    let timerId = null;

    const isVisible = () =>
      typeof document === 'undefined' || document.visibilityState !== 'hidden';

    const stop = () => {
      if (timerId !== null) {
        clearInterval(timerId);
        timerId = null;
      }
    };

    const start = () => {
      if (timerId === null) {
        timerId = setInterval(() => callbackRef.current?.(), intervalMs);
      }
    };

    const handleVisibilityChange = () => {
      if (isVisible()) {
        // Catch up on whatever was missed before resuming the schedule.
        callbackRef.current?.();
        start();
      } else {
        stop();
      }
    };

    if (isVisible()) start();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [intervalMs, enabled]);
}

export default useVisiblePolling;
